import { useCallback, useEffect, useRef, useState } from "react";

function prefersReducedMotion() {
  return typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Lets an overlay play its exit animation before it unmounts. Returns
 * `closing` (apply an "is-closing" class while true) and `requestClose`,
 * which flips `closing` on and calls `onClose` once `duration` ms pass.
 * Users who prefer reduced motion get an immediate close.
 */
export function useAnimatedClose(onClose, duration = 200) {
  const [closing, setClosing] = useState(false);
  const timerRef = useRef(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const requestClose = useCallback(() => {
    if (timerRef.current !== null) return;
    if (prefersReducedMotion()) {
      onCloseRef.current();
      return;
    }
    setClosing(true);
    timerRef.current = setTimeout(() => onCloseRef.current(), duration);
  }, [duration]);

  return { closing, requestClose };
}
